import { styled } from "goober";
import { connectorsByIdentifier } from "../../shared/connectors";
import { playClick } from "../lib/sounds";
import {
  ConnectorDropdown,
  Divider,
  Row,
  SectionLabel,
  SubtleButton,
} from "./ConnectorDropdown";
import { type AppInfo, ConnectorOption, ConnectorRow } from "./ConnectorRow";
import { Icon } from "./Icon";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { navigate, routes } from "../lib/urls";

const Field = styled("button")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 12px;
  background: var(--surface-primary);
  color: var(--text-primary);
  outline: 0.5px solid var(--border-alpha);
  border: 0;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: var(--alpha4);
  }

  &:disabled {
    background: var(--alpha4);
    cursor: not-allowed;
  }
`;

export type AddNewOption = {
  label: string;
  subtitle?: string;
  template?: { name: string; identifier: string };
  onClick: () => void;
};

type Props = {
  apps: AppInfo[];
  value: string | null;
  selectedName: string | null;
  onChange: (id: string | null) => void;
  placeholder: string;
  addNew: AddNewOption[];
  disabled?: boolean;
  appSubtitle?: (app: AppInfo) => React.ReactNode;
};

export function ConnectorSelect({
  apps,
  value,
  selectedName,
  onChange,
  placeholder,
  addNew,
  disabled,
  appSubtitle,
}: Props) {
  return (
    <ConnectorDropdown
      style={{ width: "100%" }}
      align="left"
      disabled={disabled}
      opener={
        <Field type="button" data-no-pill="" disabled={disabled}>
          <Text
            ellipsis
            variant="text-regular"
            color={
              selectedName ? "var(--text-primary)" : "var(--text-secondary)"
            }
          >
            {selectedName ?? placeholder}
          </Text>
          <span style={{ opacity: 0.3 }}>
            <Icon name="chevron-down" size={12} />
          </span>
        </Field>
      }
    >
      {(query, close) => {
        const q = query.toLowerCase().trim();
        const filtered = q
          ? apps.filter((app) => {
              const def = connectorsByIdentifier[app.identifier];
              return (
                app.name.toLowerCase().includes(q) ||
                def?.keywords?.toLowerCase().includes(q) ||
                def?.authLabel?.toLowerCase().includes(q)
              );
            })
          : apps;
        const filteredAddNew = q
          ? addNew.filter(
              (option) =>
                option.label.toLowerCase().includes(q) ||
                option.subtitle?.toLowerCase().includes(q)
            )
          : addNew;
        return (
          <>
            {filtered.map((app) => (
              <ConnectorOption
                key={app.id}
                app={app}
                close={close}
                onClick={() => onChange(app.id === value ? null : app.id)}
                secondary={appSubtitle?.(app)}
                idle={
                  app.id === value ? (
                    <Icon
                      name="check"
                      size={12}
                      color="var(--text-secondary)"
                    />
                  ) : undefined
                }
                hover={
                  <Tooltip content="Open connector">
                    <SubtleButton
                      icon="arrow-up-right"
                      variant="secondary"
                      onClick={() => {
                        navigate(routes.modal.connector, { id: app.id });
                        close();
                      }}
                    />
                  </Tooltip>
                }
              />
            ))}
            {filtered.length > 0 && filteredAddNew.length > 0 && <Divider />}
            {filteredAddNew.length > 0 && (
              <SectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  Add new
                </Text>
              </SectionLabel>
            )}
            {filteredAddNew.map((option) => (
              <Row
                key={option.template?.identifier ?? option.label}
                onClick={() => {
                  playClick();
                  option.onClick();
                  close();
                }}
              >
                {option.template ? (
                  <ConnectorRow
                    template={option.template}
                    name={option.label}
                    secondary={option.subtitle}
                  />
                ) : (
                  <>
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon name="plus" size={12} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Text
                        variant="text-regular"
                        color="var(--text-primary)"
                        ellipsis
                      >
                        {option.label}
                      </Text>
                      {option.subtitle && (
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {option.subtitle}
                        </Text>
                      )}
                    </div>
                  </>
                )}
              </Row>
            ))}
            {filtered.length === 0 && filteredAddNew.length === 0 && (
              <SectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  No connectors found.
                </Text>
              </SectionLabel>
            )}
          </>
        );
      }}
    </ConnectorDropdown>
  );
}
